import { SupabaseClient } from '@supabase/supabase-js';
import { ValidationError } from '@repo/errors';
import { supabase as supabaseClient } from '../client/browser';
import { v4 as uuidv4 } from 'uuid';

export interface AssessmentData {
  episodeId?: string | null;  // Optional for couples/group therapy which use groupId instead
  groupId?: string | null;
  sessionId?: string;
  proId: string;
  date?: string;
  type: 'initial_individual' | 'initial_couples' | 'initial_group' | 'progress' | 'discharge';
  status: 'DRAFT' | 'SUBMITTED';
  signed: boolean;
  signedAt?: string;
  
  // Consent fields
  confidentiality?: boolean;
  cancellation?: boolean;
  space?: boolean;
  contact?: boolean;
  location?: boolean;
  residency?: boolean;
  recording?: boolean;
  emergency?: boolean;
  
  // Assessment content (flexible JSON for different templates)
  assessmentContent?: any;
  
  // Legacy fields (keeping for backward compatibility)
  pLiving?: string;
  issues?: string;
  hDevelopmental?: string;
  hEducational?: string;
  mental?: string;
  hTrauma?: string;
  hSocial?: string;
  pValues?: string;
  pStressors?: string;
  cope?: string;
  substance?: string;
  safety?: string;
  goal?: string;
  treatmentPlan?: string;
  notes?: string;
  fit?: boolean;
  frequency?: number;
  start?: string;
  commitment?: string;
  
  // Couples-specific fields
  parenting?: string;
  sex?: string;
  time?: string;
  strengths?: string;
  communication?: string;
  conflict?: string;
  career?: string;
}

export interface Assessment extends AssessmentData {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

/**
 * Create a new assessment
 */
export async function createAssessment(
  data: AssessmentData,
  supabase?: SupabaseClient
): Promise<Assessment> {
  const client = supabase || supabaseClient;
  
  const now = new Date().toISOString();
  
  console.log('[createAssessment] Creating assessment with data:', {
    episodeId: data.episodeId,
    sessionId: data.sessionId,
    type: data.type,
    status: data.status
  });
  
  const { data: assessment, error } = await client
    .from('assessments')
    .insert({
      id: uuidv4(),
      ...data,
      date: data.date || now,
      createdAt: now,
      updatedAt: now
    })
    .select()
    .single();
  
  if (error) {
    console.error('Error creating assessment:', error);
    throw error;
  }
  
  console.log('[createAssessment] Successfully created assessment:', {
    assessmentId: assessment.id,
    type: assessment.type,
    status: assessment.status
  });
  
  return assessment;
}

/**
 * Update an existing assessment
 */
export async function updateAssessment(
  id: string,
  data: Partial<AssessmentData>,
  supabase?: SupabaseClient
): Promise<Assessment> {
  const client = supabase || supabaseClient;
  
  // Check if assessment is already signed/submitted (Canadian standards compliance)
  const { data: existingAssessment, error: fetchError } = await client
    .from('assessments')
    .select('status, signed')
    .eq('id', id)
    .single();

  if (fetchError) {
    console.error('Error fetching assessment for update:', fetchError);
    throw fetchError;
  }

  // Allow signing a draft (transitioning from DRAFT to SUBMITTED)
  const isSigningDraft = existingAssessment.status === 'DRAFT' &&
                         data.status === 'SUBMITTED' &&
                         data.signed === true;

  console.log('[updateAssessment] Update check:', {
    assessmentId: id,
    existingStatus: existingAssessment.status,
    existingSigned: existingAssessment.signed,
    newStatus: data.status,
    newSigned: data.signed,
    isSigningDraft,
    willBlock: !isSigningDraft && (existingAssessment.status === 'SUBMITTED' || existingAssessment.signed)
  });

  // Prevent editing of already signed/submitted assessments (unless it's the initial signing)
  if (!isSigningDraft && (existingAssessment.status === 'SUBMITTED' || existingAssessment.signed)) {
    console.error('[updateAssessment] Blocked: Cannot edit signed assessment', {
      assessmentId: id,
      existingStatus: existingAssessment.status,
      existingSigned: existingAssessment.signed
    });
    throw new ValidationError('Cannot edit signed or submitted assessments. Per Canadian therapy documentation standards, signed assessments are immutable.');
  }
  
  const { data: assessment, error } = await client
    .from('assessments')
    .update({
      ...data,
      updatedAt: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();
  
  if (error) {
    console.error('Error updating assessment:', error);
    throw error;
  }
  
  console.log('[updateAssessment] Updated assessment:', {
    assessmentId: id,
    updatedAt: assessment.updatedAt
  });
  
  return assessment;
}

/**
 * Get an assessment by ID
 */
export async function getAssessment(
  id: string,
  supabase?: SupabaseClient
): Promise<Assessment | null> {
  const client = supabase || supabaseClient;
  
  const { data: assessment, error } = await client
    .from('assessments')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') {
      // No rows found
      return null;
    }
    console.error('Error fetching assessment:', error);
    throw error;
  }
  
  return assessment;
}

/**
 * Get assessment by session ID
 */
export async function getAssessmentBySessionId(
  sessionId: string,
  supabase?: SupabaseClient
): Promise<Assessment | null> {
  const client = supabase || supabaseClient;
  
  const { data: assessment, error } = await client
    .from('assessments')
    .select('*')
    .eq('sessionId', sessionId)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') {
      // No rows found
      return null;
    }
    console.error('Error fetching assessment by session:', error);
    throw error;
  }
  
  return assessment;
}

/**
 * Get all assessments for an episode
 */
export async function getAssessmentsByEpisode(
  episodeId: string,
  supabase?: SupabaseClient
): Promise<Assessment[]> {
  const client = supabase || supabaseClient;
  
  const { data: assessments, error } = await client
    .from('assessments')
    .select('*')
    .eq('episodeId', episodeId)
    .order('date', { ascending: false });
  
  if (error) {
    console.error('Error fetching assessments for episode:', error);
    throw error;
  }
  
  return assessments || [];
}

/**
 * Get all assessments for a therapy group (couples/group therapy)
 */
export async function getAssessmentsByGroup(
  groupId: string,
  supabase?: SupabaseClient
): Promise<Assessment[]> {
  const client = supabase || supabaseClient;

  const { data: assessments, error } = await client
    .from('assessments')
    .select('*')
    .eq('groupId', groupId)
    .order('date', { ascending: false });

  if (error) {
    console.error('Error fetching assessments for group:', error);
    throw error;
  }

  return assessments || [];
}

/**
 * Delete an assessment (only if in DRAFT status)
 */
export async function deleteAssessment(
  id: string,
  supabase?: SupabaseClient
): Promise<void> {
  const client = supabase || supabaseClient;
  
  // First check if the assessment is in DRAFT status
  const { data: assessment, error: fetchError } = await client
    .from('assessments')
    .select('status')
    .eq('id', id)
    .single();
  
  if (fetchError) {
    console.error('Error fetching assessment for deletion:', fetchError);
    throw fetchError;
  }
  
  if (assessment.status !== 'DRAFT') {
    throw new ValidationError('Cannot delete submitted assessments');
  }
  
  const { error } = await client
    .from('assessments')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error('Error deleting assessment:', error);
    throw error;
  }
}